import { useEffect, useState, useCallback } from 'react';
import { useApi } from '../../../utils/hooks';
import { Helpers } from '../../../utils/helpers';
import { SortDirection } from '../../../utils/constants/enums';
import {
  IPagedResult,
  IWorkHoursReportDto,
  IWorkHoursReportRequest,
  IWorkHoursSearchRequest,
  ISystemCodeByTypeDto
} from '../../../utils/constants/interfaces';
import { ReportColumn, SearchCriterion } from '../../../components/ui/ReportActions';
import { useReportFormatters } from '../../../utils/hooks/useReportFormatters';

const emptySearchResults: IPagedResult<IWorkHoursReportDto[]> = {
  currentPage: 1,
  results: [],
  pageSize: 1,
  pageCount: 1,
  resultCount: 0
};

const useWorkHoursReport = () => {
  const formatters = useReportFormatters();

  const defaultSearchParams: IWorkHoursSearchRequest = {
    // C# WorkHoursReportRequest properties
    clubMemberId: null,
    firstName: '',
    lastName: '',
    workHoursDateFrom: null,
    workHoursDateThru: null,
    enteredDateFrom: null,
    enteredDateThru: null,
    updatedDateFrom: null,
    updatedDateThru: null,
    // Pagination and sorting
    sortDirection: SortDirection.ASCENDING,
    sortField: 'id', // Default sort by WH_ID ascending
    pageSize: 50,
    page: 1
  };

  const api = useApi();
  const [loading, setLoading] = useState<boolean>(false);
  const [allResults, setAllResults] = useState<IWorkHoursReportDto[]>([]);
  const [workHoursPagedResults, setWorkHoursPagedResults] =
    useState<IPagedResult<IWorkHoursReportDto[]>>(emptySearchResults);
  const [searchParameters, setSearchParameters] =
    useState<IWorkHoursSearchRequest>(defaultSearchParams);
  const [hasPerformedSearch, setHasPerformedSearch] = useState<boolean>(false);

  // Function to apply client-side sorting and pagination
  const applyClientSidePaginationAndSort = useCallback(() => {
    if (allResults.length === 0) {
      setWorkHoursPagedResults(emptySearchResults);
      return;
    }

    // Apply sorting
    const sortedResults = [...allResults].sort((a, b) => {
      const field = searchParameters.sortField;
      const currentSortDirection: SortDirection = searchParameters.sortDirection;
      let aValue: string | number | Date | ISystemCodeByTypeDto | null =
        a[field as keyof IWorkHoursReportDto];
      let bValue: string | number | Date | ISystemCodeByTypeDto | null =
        b[field as keyof IWorkHoursReportDto];

      // Handle nested committee field
      if (field === 'committee') {
        aValue = a.committee?.description || '';
        bValue = b.committee?.description || '';
      }

      // Handle dates with proper type checking
      if (field === 'workHoursDateUtc') {
        aValue = aValue ? new Date(aValue as string | number | Date) : new Date(0);
        bValue = bValue ? new Date(bValue as string | number | Date) : new Date(0);
      }

      // Convert to strings for comparison if needed
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      // Handle null values for comparison
      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return currentSortDirection === SortDirection.ASCENDING ? -1 : 1;
      if (bValue === null) return currentSortDirection === SortDirection.ASCENDING ? 1 : -1;

      if (aValue < bValue) {
        return currentSortDirection === SortDirection.ASCENDING ? -1 : 1;
      }
      if (aValue > bValue) {
        return currentSortDirection === SortDirection.ASCENDING ? 1 : -1;
      }
      return 0;
    });

    // Apply pagination
    const totalResults = sortedResults.length;
    const pageCount = Math.ceil(totalResults / searchParameters.pageSize);
    const startIndex = (searchParameters.page - 1) * searchParameters.pageSize;
    const endIndex = startIndex + searchParameters.pageSize;
    const pageResults = sortedResults.slice(startIndex, endIndex);

    const pagedResult: IPagedResult<IWorkHoursReportDto[]> = {
      currentPage: searchParameters.page,
      results: pageResults,
      pageSize: searchParameters.pageSize,
      pageCount: pageCount,
      resultCount: totalResults
    };

    setWorkHoursPagedResults(pagedResult);
  }, [
    allResults,
    searchParameters.sortField,
    searchParameters.sortDirection,
    searchParameters.page,
    searchParameters.pageSize
  ]);

  const search = useCallback(async (): Promise<void> => {
    setLoading(true);
    setHasPerformedSearch(true);

    // Build the request body to match C# WorkHoursReportRequest exactly
    const requestBody: IWorkHoursReportRequest = {
      clubMemberId: searchParameters.clubMemberId,
      firstName: searchParameters.firstName || '',
      lastName: searchParameters.lastName || '',
      workHoursDateFrom: searchParameters.workHoursDateFrom,
      workHoursDateThru: searchParameters.workHoursDateThru,
      enteredDateFrom: searchParameters.enteredDateFrom,
      enteredDateThru: searchParameters.enteredDateThru,
      updatedDateFrom: searchParameters.updatedDateFrom,
      updatedDateThru: searchParameters.updatedDateThru
    };

    const endpoint = '/members/workhours';
    const apiResponse = await api.post<IWorkHoursReportDto[]>(
      endpoint,
      JSON.stringify(requestBody)
    );

    if (apiResponse.isSuccessStatusCode && apiResponse.response) {
      // Store all results for client-side pagination
      setAllResults(apiResponse.response);
    } else {
      // Handle error
      setAllResults([]);
      setWorkHoursPagedResults(emptySearchResults);
    }
    setLoading(false);
  }, [
    api,
    searchParameters.clubMemberId,
    searchParameters.firstName,
    searchParameters.lastName,
    searchParameters.workHoursDateFrom,
    searchParameters.workHoursDateThru,
    searchParameters.enteredDateFrom,
    searchParameters.enteredDateThru,
    searchParameters.updatedDateFrom,
    searchParameters.updatedDateThru
  ]);

  // Apply pagination and sorting whenever allResults or pagination/sort parameters change
  useEffect(() => {
    applyClientSidePaginationAndSort();
  }, [applyClientSidePaginationAndSort]);

  const triggerSearch = () => {
    void search();
  };

  const resetSearch = () => {
    setSearchParameters(defaultSearchParams);
    setAllResults([]);
    setWorkHoursPagedResults(emptySearchResults);
    setHasPerformedSearch(false);
  };

  const changePage = (page: number) => {
    setSearchParameters({ ...searchParameters, page: page });
  };

  const changeSort = (sortField: string) => {
    const currentSortDirection: SortDirection = searchParameters.sortDirection;
    const sortDirection =
      searchParameters.sortField === sortField
        ? currentSortDirection === SortDirection.ASCENDING
          ? SortDirection.DESCENDING
          : SortDirection.ASCENDING
        : SortDirection.ASCENDING;
    setSearchParameters({
      ...searchParameters,
      sortField: sortField,
      sortDirection: sortDirection,
      page: 1 // Reset to page 1 when sorting
    });
  };

  const updateFilter = (field: string, value: string) => {
    const localSearchParams = { ...searchParameters };
    localSearchParams.page = 1; // Reset to page 1 on new search

    // Handle different field types
    if (field === 'clubMemberId') {
      localSearchParams.clubMemberId = value ? parseInt(value) : null;
    } else if (field === 'firstName') {
      localSearchParams.firstName = value;
    } else if (field === 'lastName') {
      localSearchParams.lastName = value;
    } else if (field === 'workHoursDateFrom') {
      localSearchParams.workHoursDateFrom = value ? new Date(value) : null;
    } else if (field === 'workHoursDateThru') {
      localSearchParams.workHoursDateThru = value ? new Date(value) : null;
    } else if (field === 'enteredDateFrom') {
      localSearchParams.enteredDateFrom = value ? new Date(value) : null;
    } else if (field === 'enteredDateThru') {
      localSearchParams.enteredDateThru = value ? new Date(value) : null;
    } else if (field === 'updatedDateFrom') {
      localSearchParams.updatedDateFrom = value ? new Date(value) : null;
    } else if (field === 'updatedDateThru') {
      localSearchParams.updatedDateThru = value ? new Date(value) : null;
    }

    setSearchParameters(localSearchParams);
  };

  const hasSearchParametersCheck = () => {
    return (
      searchParameters.clubMemberId !== null ||
      !Helpers.isNullOrWhiteSpace(searchParameters.firstName) ||
      !Helpers.isNullOrWhiteSpace(searchParameters.lastName) ||
      searchParameters.workHoursDateFrom !== null ||
      searchParameters.workHoursDateThru !== null ||
      searchParameters.enteredDateFrom !== null ||
      searchParameters.enteredDateThru !== null ||
      searchParameters.updatedDateFrom !== null ||
      searchParameters.updatedDateThru !== null
    );
  };

  // Define column configuration for exports
  const getReportColumns = (): ReportColumn<IWorkHoursReportDto>[] => [
    { key: 'id', header: 'WH_ID' },
    { key: 'clubMemberId', header: 'CM_ID' },
    { key: 'fullName', header: 'Name' },
    {
      key: 'workHoursDateUtc',
      header: 'Year',
      formatter: (value) => formatters.formatYear(value as Date | string | null)
    },
    {
      key: 'workHoursDateUtc',
      header: 'Date',
      formatter: (value) => formatters.formatDate(value as Date | string | null)
    },
    {
      key: 'workHoursAmount',
      header: 'Hours',
      formatter: (value) => formatters.formatNumber(value as number | null, 2)
    },
    {
      key: 'committee',
      header: 'Committee',
      formatter: (value) => formatters.formatCommittee(value as { description?: string } | null)
    },
    { key: 'activity', header: 'Activity' }
  ];

  // Calculate work hours totals
  const getWorkHoursTotals = () => {
    const totalRecords = allResults.length;
    const distinctMemberships = new Set(allResults.map((record) => record.clubMemberId)).size;
    const totalHours = allResults.reduce((sum, record) => sum + record.workHoursAmount, 0);

    return {
      totalRecords,
      distinctMemberships,
      totalHours
    };
  };

  // Build search criteria for print display
  const getSearchCriteria = (): SearchCriterion[] => {
    const criteria: SearchCriterion[] = [];

    if (searchParameters.clubMemberId !== null) {
      criteria.push({ label: 'CM ID', value: searchParameters.clubMemberId.toString() });
    }

    if (!Helpers.isNullOrWhiteSpace(searchParameters.firstName)) {
      criteria.push({ label: 'First Name', value: searchParameters.firstName });
    }

    if (!Helpers.isNullOrWhiteSpace(searchParameters.lastName)) {
      criteria.push({ label: 'Last Name', value: searchParameters.lastName });
    }

    if (searchParameters.workHoursDateFrom) {
      criteria.push({
        label: 'Work Date From',
        value: formatters.formatDate(searchParameters.workHoursDateFrom)
      });
    }

    if (searchParameters.workHoursDateThru) {
      criteria.push({
        label: 'Work Date Thru',
        value: formatters.formatDate(searchParameters.workHoursDateThru)
      });
    }

    if (searchParameters.enteredDateFrom) {
      criteria.push({
        label: 'Entered Date From',
        value: formatters.formatDate(searchParameters.enteredDateFrom)
      });
    }

    if (searchParameters.enteredDateThru) {
      criteria.push({
        label: 'Entered Date Thru',
        value: formatters.formatDate(searchParameters.enteredDateThru)
      });
    }

    if (searchParameters.updatedDateFrom) {
      criteria.push({
        label: 'Updated Date From',
        value: formatters.formatDate(searchParameters.updatedDateFrom)
      });
    }

    if (searchParameters.updatedDateThru) {
      criteria.push({
        label: 'Updated Date Thru',
        value: formatters.formatDate(searchParameters.updatedDateThru)
      });
    }

    return criteria;
  };

  return {
    loading,
    searchParameters,
    workHoursPagedResults,
    allResults, // Export the full dataset for CSV/Print
    updateFilter,
    hasSearchParameters: hasSearchParametersCheck,
    hasPerformedSearch,
    changePage,
    changeSort,
    setSearchParameters,
    resetSearch,
    triggerSearch,
    getReportColumns, // Export column configuration
    getSearchCriteria, // Export search criteria for print
    getWorkHoursTotals // Export totals calculation
  };
};

export default useWorkHoursReport;
